import React from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    TableCell,
    TableRow,
    Typography
} from "@mui/material";
import {
    CalendarTodayRounded,
    CancelRounded,
    CheckCircleRounded,
    HourglassBottomRounded,
    PictureAsPdfRounded,
    ReceiptLongRounded
} from "@mui/icons-material";
import { getUserFixedName, toPriceWithComma } from "../../../helper/utils";

export const renderIncreaseStatusChip = (status) => {
    switch (status) {
        case "CONFIRMED":
            return (
                <Chip
                    icon={<CheckCircleRounded sx={{ fontSize: 16 }} />}
                    label="تکمیل شده"
                    size="small"
                    sx={{
                        fontWeight: 700,
                        borderRadius: 1.8,
                        bgcolor: 'rgba(16, 185, 129, 0.1)',
                        color: 'success.main',
                        border: '1px solid',
                        borderColor: 'rgba(16, 185, 129, 0.2)'
                    }}
                />
            );
        case "REQUESTED":
            return (
                <Chip
                    icon={<HourglassBottomRounded sx={{ fontSize: 16 }} />}
                    label="در انتظار بررسی"
                    size="small"
                    sx={{
                        fontWeight: 700,
                        borderRadius: 1.8,
                        bgcolor: 'rgba(245, 158, 11, 0.1)',
                        color: 'warning.main',
                        border: '1px solid',
                        borderColor: 'rgba(245, 158, 11, 0.2)'
                    }}
                />
            );
        case "DRAFT":
            return (
                <Chip
                    icon={<ReceiptLongRounded sx={{ fontSize: 16 }} />}
                    label="پیش‌فاکتور"
                    size="small"
                    sx={{
                        fontWeight: 700,
                        borderRadius: 1.8,
                        bgcolor: 'rgba(28, 139, 156, 0.1)',
                        color: 'primary.main',
                        border: '1px solid',
                        borderColor: 'rgba(28, 139, 156, 0.2)'
                    }}
                />
            );
        case "REJECTED":
        case "CANCELED":
            return (
                <Chip
                    icon={<CancelRounded sx={{ fontSize: 16 }} />}
                    label={status === "CANCELED" ? "لغو شده" : "رد شده"}
                    size="small"
                    sx={{
                        fontWeight: 700,
                        borderRadius: 1.8,
                        bgcolor: 'rgba(239, 68, 68, 0.1)',
                        color: 'error.main',
                        border: '1px solid',
                        borderColor: 'rgba(239, 68, 68, 0.2)'
                    }}
                />
            );
        default:
            return (
                <Chip
                    label={status || "نامشخص"}
                    size="small"
                    sx={{ fontWeight: 700, borderRadius: 1.8 }}
                />
            );
    }
};

const _IncreaseHistoryTableRow = ({ row, downloadingId, onGetInvoice }) => {
    const serialShort = row.Serial?.Serial ? row.Serial.Serial.split("-")[0] : row.Serial;
    const isConfirmed = row.DepositStatus === "CONFIRMED";
    const isDownloading = downloadingId === row.Id;

    return (
        <TableRow
            hover
            sx={{
                transition: 'background-color 0.15s ease',
                '&:last-child td, &:last-child th': { border: 0 }
            }}
        >
            <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {serialShort || "—"}
                </Typography>
            </TableCell>

            <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 800, color: 'text.primary' }}>
                    {toPriceWithComma(row.Amount)}
                    <Typography component="span" variant="caption" sx={{ color: 'text.secondary', mr: 0.5 }}>
                        تومان
                    </Typography>
                </Typography>
            </TableCell>

            <TableCell>
                {renderIncreaseStatusChip(row.DepositStatus)}
            </TableCell>

            <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, color: 'text.secondary' }}>
                    <CalendarTodayRounded sx={{ fontSize: 13 }} />
                    <Typography variant="caption">
                        {new Date(row.CreatedDate).toLocaleDateString('fa-IR', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                        })}
                    </Typography>
                </Box>
            </TableCell>

            <TableCell>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {row?.CreatorUser ? getUserFixedName(row.CreatorUser) : "سیستم"}
                </Typography>
            </TableCell>

            <TableCell align="left">
                {isConfirmed ? (
                    <Button
                        variant="outlined"
                        size="small"
                        disabled={isDownloading}
                        startIcon={isDownloading ? <CircularProgress size={14} /> : <PictureAsPdfRounded sx={{ ml: 0.5 }} />}
                        onClick={(e) => onGetInvoice(e, row)}
                        sx={{
                            borderRadius: 2,
                            fontWeight: 700,
                            fontSize: '0.75rem',
                            py: 0.5,
                            px: 1.5
                        }}
                    >
                        دریافت فاکتور
                    </Button>
                ) : (
                    <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                        —
                    </Typography>
                )}
            </TableCell>
        </TableRow>
    );
};

export default _IncreaseHistoryTableRow;
